export type BreadcrumbItem = {
  label: string;
  to?: string;
};

export type BreadcrumbSection =
  | 'OPERATIONS'
  | 'PROMOTIONS'
  | 'PRICING'
  | 'AVAILABILITY'
  | 'FLEET'
  | 'ADMINISTRATION'
  | 'COMPONENTS';

export type EntityRouteConfig = {
  section: BreadcrumbSection;
  listPath: string;
  listLabel: string;
  description?: string;
  labels: {
    add?: string;
    edit?: string;
    detail?: string;
    view?: string;
  };
  /** `edit` = /base/:id ; `view` = /base/:id/view ; default uses /edit/:id and /detail/:id */
  idStyle?: 'param' | 'edit-segment' | 'detail-segment';
};

export type StandaloneRouteConfig = {
  section?: BreadcrumbSection;
  label: string;
  description?: string;
};

const entityRoutesUnsorted: EntityRouteConfig[] = [
  {
    section: 'OPERATIONS',
    listPath: '/bookings',
    listLabel: 'Booking Log',
    description: 'View and manage customer bookings',
    labels: {
      add: 'Add Booking',
      edit: 'Edit Booking',
      view: 'Booking Details',
    },
    idStyle: 'param',
  },
  {
    section: 'OPERATIONS',
    listPath: '/drivers',
    listLabel: 'Drivers',
    description: 'Manage driver profiles and assignments',
    labels: { add: 'Add Driver', edit: 'Edit Driver', view: 'Driver Details' },
    idStyle: 'param',
  },
  {
    section: 'OPERATIONS',
    listPath: '/trip-issues',
    listLabel: 'Trip Issues',
    description: 'Track and resolve trip-related issues',
    labels: { add: 'Add Trip Issue', edit: 'Edit Trip Issue' },
    idStyle: 'edit-segment',
  },
  {
    section: 'OPERATIONS',
    listPath: '/ride-reviews',
    listLabel: 'Ratings',
    description: 'Customer ratings and ride reviews',
    labels: { add: 'Add Rating', edit: 'Edit Rating' },
    idStyle: 'edit-segment',
  },
  {
    section: 'OPERATIONS',
    listPath: '/statements',
    listLabel: 'Statements',
    description: 'Financial statements and payouts',
    labels: { add: 'Add Statement', edit: 'Edit Statement' },
    idStyle: 'edit-segment',
  },
  {
    section: 'PROMOTIONS',
    listPath: '/service-pauses',
    listLabel: 'Pause Availability',
    description: 'Temporary service pause windows',
    labels: {
      add: 'Add Service Pause',
      edit: 'Edit Service Pause',
      detail: 'Service Pause Details',
    },
  },
  {
    section: 'PROMOTIONS',
    listPath: '/flash-sales',
    listLabel: 'Flash Sales',
    description: 'Limited-time promotional pricing',
    labels: { add: 'Add Flash Sale', edit: 'Edit Flash Sale' },
    idStyle: 'edit-segment',
  },
  {
    section: 'PROMOTIONS',
    listPath: '/coupons',
    listLabel: 'Coupons',
    description: 'Promotional coupon codes for the booking websites',
    labels: { add: 'Add Coupon', edit: 'Edit Coupon' },
    idStyle: 'edit-segment',
  },
  {
    section: 'PROMOTIONS',
    listPath: '/dead-legs',
    listLabel: 'Dead Leg Discounts',
    description: 'Discounts for empty return legs linked to full-price bookings',
    labels: {
      add: 'Add Dead Leg Discount',
      edit: 'Edit Dead Leg Discount',
    },
  },
  {
    section: 'PROMOTIONS',
    listPath: '/route-fixed-fares',
    listLabel: 'Route Fixed Fares',
    description: 'Fixed prices between route endpoints',
    labels: {
      add: 'Add Route Fixed Fare',
      edit: 'Edit Route Fixed Fare',
      detail: 'Route Fixed Fare Details',
    },
  },
  {
    section: 'PRICING',
    listPath: '/location-charges',
    listLabel: 'Location Charges',
    description: 'Extra charges when pickup or drop-off is in a postcode area (e.g. congestion zones)',
    labels: { add: 'Add Location Charge', edit: 'Edit Location Charge', detail: 'Location Charge Details' },
  },
  {
    section: 'PRICING',
    listPath: '/location-prices',
    listLabel: 'Location Prices (LPs)',
    description: 'Fixed prices between postcode zones with radius',
    labels: {},
  },
  {
    section: 'PRICING',
    listPath: '/postcode-area-prices',
    listLabel: 'Postcode Area Prices (PAPs)',
    description: 'Fixed prices between postcode areas by vehicle size',
    labels: {},
  },
  {
    section: 'PRICING',
    listPath: '/zones',
    listLabel: 'Zones',
    description: 'Geographic zones for pricing',
    labels: { add: 'Add Zone', edit: 'Edit Zone', detail: 'Zone Details' },
  },
  {
    section: 'PRICING',
    listPath: '/meet-greet-charges',
    listLabel: 'Meet & Greet Charges',
    description: 'Airport meet and greet fees by pickup location (0.2 mile radius)',
    labels: { add: 'Add Meet & Greet Charge', edit: 'Edit Meet & Greet Charge' },
    idStyle: 'edit-segment',
  },
  {
    section: 'PRICING',
    listPath: '/more-pricing-options',
    listLabel: 'More Pricing Options',
    description: 'Free pickup areas, wait & return, overnight, holidays, vias, and cash radius',
    labels: {},
  },
  {
    section: 'PRICING',
    listPath: '/luggage',
    listLabel: 'Luggage Types',
    description: 'Luggage types and pricing modifiers',
    labels: { add: 'Add Luggage', edit: 'Edit Luggage', detail: 'Luggage Details' },
  },
  {
    section: 'PRICING',
    listPath: '/vehicle-time-prices',
    listLabel: 'Time Prices',
    description: 'Time-based vehicle pricing rules',
    labels: {
      add: 'Add Time Price',
      edit: 'Edit Time Price',
      view: 'Time Price Details',
    },
    idStyle: 'param',
  },
  {
    section: 'PRICING',
    listPath: '/special-prices',
    listLabel: 'Special Prices',
    description: 'Date-range and conditional special pricing',
    labels: {
      add: 'Add Special Price',
      edit: 'Edit Special Price',
      detail: 'Special Price Details',
    },
  },
  {
    section: 'FLEET',
    listPath: '/vehicle-type',
    listLabel: 'Vehicle Types',
    description: 'Vehicle categories and capacity rules',
    labels: {
      add: 'Add Vehicle Type',
      edit: 'Edit Vehicle Type',
      detail: 'Vehicle Type Details',
    },
  },
  {
    section: 'FLEET',
    listPath: '/vehicles',
    listLabel: 'Vehicles',
    description: 'Fleet vehicles linked to your account',
    labels: { add: 'Add Vehicle', edit: 'Edit Vehicle', view: 'Vehicle Details' },
    idStyle: 'param',
  },
  {
    section: 'FLEET',
    listPath: '/customers',
    listLabel: 'Customers',
    description: 'Registered customer accounts',
    labels: {
      add: 'Add Customer',
      edit: 'Edit Customer',
      view: 'Customer Details',
    },
    idStyle: 'param',
  },
  {
    section: 'ADMINISTRATION',
    listPath: '/admin-user',
    listLabel: 'Admin Users',
    description: 'Dashboard administrator accounts',
    labels: {
      add: 'Add Admin User',
      edit: 'Edit Admin User',
      detail: 'Admin User Details',
    },
  },
  {
    section: 'ADMINISTRATION',
    listPath: '/role',
    listLabel: 'Roles',
    description: 'User role definitions',
    labels: { add: 'Add Role', edit: 'Edit Role' },
    idStyle: 'edit-segment',
  },
  {
    section: 'ADMINISTRATION',
    listPath: '/permission',
    listLabel: 'Permissions',
    description: 'Granular permission definitions',
    labels: { add: 'Add Permission', edit: 'Edit Permission' },
    idStyle: 'edit-segment',
  },
  {
    section: 'ADMINISTRATION',
    listPath: '/user-role-mapping',
    listLabel: 'User Role Mappings',
    description: 'Assign roles to admin users',
    labels: { add: 'Add User Role Mapping', edit: 'Edit User Role Mapping' },
    idStyle: 'edit-segment',
  },
  {
    section: 'ADMINISTRATION',
    listPath: '/role-permission-mapping',
    listLabel: 'Role Permission Mappings',
    description: 'Assign permissions to roles',
    labels: {
      add: 'Add Role Permission Mapping',
      edit: 'Edit Role Permission Mapping',
    },
    idStyle: 'edit-segment',
  },
  {
    section: 'ADMINISTRATION',
    listPath: '/admin-user-address',
    listLabel: 'User Addresses',
    description: 'Addresses linked to admin users',
    labels: { add: 'Add Address', edit: 'Edit Address' },
    idStyle: 'edit-segment',
  },
];

/** Longest paths first when matching */
export const entityRoutes: EntityRouteConfig[] = [...entityRoutesUnsorted].sort(
  (a, b) => b.listPath.length - a.listPath.length,
);

export const standaloneRoutes: Record<string, StandaloneRouteConfig> = {
  '/': {
    label: 'Dashboard',
    description: 'Overview of bookings, drivers, fleet, revenue, and promotions',
  },
  '/price-checker': {
    section: 'PROMOTIONS',
    label: 'Price Checker',
    description: 'Compare regional benchmark routes with your PAPs and check fares',
  },
  '/per-mile-prices': {
    section: 'PRICING',
    label: 'Per Mile Prices',
    description: 'Mileage bands, uplift pricing, and fare calculator',
  },
  '/notice-periods': {
    section: 'AVAILABILITY',
    label: 'Notice Periods',
    description: 'Minimum notice per vehicle size and postcode lead time overrides',
  },
  '/trip-range': {
    section: 'AVAILABILITY',
    label: 'Trip Range',
    description: 'How far in advance trips can be booked',
  },
  '/operating-hours': {
    section: 'AVAILABILITY',
    label: 'Operating Hours',
    description: 'First and final pickup times for each day of the week',
  },
  '/number-of-vehicles': {
    section: 'AVAILABILITY',
    label: 'Number of Vehicles',
    description: 'Concurrent vehicles available per fleet type and day of week',
  },
  '/profile': {
    section: 'ADMINISTRATION',
    label: 'Profile',
    description: 'Your account profile and preferences',
  },
  '/manage-sessions': {
    section: 'ADMINISTRATION',
    label: 'Manage Sessions',
    description: 'View and revoke active admin login sessions',
  },
  '/calendar': {
    section: 'OPERATIONS',
    label: 'Calendar',
    description: 'Schedule and calendar view',
  },
  '/blank': { section: 'COMPONENTS', label: 'Blank Page' },
  '/form-elements': { section: 'COMPONENTS', label: 'Form Elements' },
  '/basic-tables': { section: 'COMPONENTS', label: 'Basic Tables' },
  '/alerts': { section: 'COMPONENTS', label: 'Alerts' },
  '/avatars': { section: 'COMPONENTS', label: 'Avatars' },
  '/badges': { section: 'COMPONENTS', label: 'Badges' },
  '/buttons': { section: 'COMPONENTS', label: 'Buttons' },
  '/images': { section: 'COMPONENTS', label: 'Images' },
  '/videos': { section: 'COMPONENTS', label: 'Videos' },
};

const sectionLabels: Record<BreadcrumbSection, string> = {
  OPERATIONS: 'Operations',
  PROMOTIONS: 'Promotions',
  PRICING: 'Pricing',
  AVAILABILITY: 'Availability',
  FLEET: 'Fleet & Customers',
  ADMINISTRATION: 'Administration',
  COMPONENTS: 'Components',
};

export type ResolvedBreadcrumb = {
  section?: BreadcrumbItem;
  parents: BreadcrumbItem[];
  pageTitle: string;
  description?: string;
};

function matchEntityAction(
  pathname: string,
  config: EntityRouteConfig,
): 'list' | 'add' | 'edit' | 'detail' | 'view' | null {
  const base = config.listPath;
  if (pathname === base) return 'list';
  if (!pathname.startsWith(`${base}/`)) return null;

  const rest = pathname.slice(base.length);
  if (rest === '/add') return 'add';

  const idStyle = config.idStyle ?? 'detail-segment';

  if (idStyle === 'edit-segment') {
    if (/^\/edit\/[^/]+$/.test(rest)) return 'edit';
    return null;
  }

  if (idStyle === 'param') {
    if (/^\/[^/]+\/view$/.test(rest)) return 'view';
    if (/^\/edit\/[^/]+$/.test(rest)) return 'edit';
    if (/^\/[^/]+$/.test(rest) && rest !== '/add') return 'edit';
    return null;
  }

  // detail-segment (default)
  if (/^\/detail\/[^/]+$/.test(rest)) return 'detail';
  if (/^\/edit\/[^/]+$/.test(rest)) return 'edit';
  return null;
}

function titleForAction(
  config: EntityRouteConfig,
  action: 'list' | 'add' | 'edit' | 'detail' | 'view',
): string {
  const { labels, listLabel } = config;
  switch (action) {
    case 'list':
      return listLabel;
    case 'add':
      return labels.add ?? `Add ${listLabel.replace(/s$/, '')}`;
    case 'edit':
      return labels.edit ?? `Edit ${listLabel.replace(/s$/, '')}`;
    case 'detail':
      return labels.detail ?? `${listLabel.replace(/s$/, '')} Details`;
    case 'view':
      return labels.view ?? `${listLabel.replace(/s$/, '')} Details`;
    default:
      return listLabel;
  }
}

export function resolveBreadcrumbs(pathname: string): ResolvedBreadcrumb | null {
  const path = pathname.replace(/\/$/, '') || '/';

  for (const entity of entityRoutes) {
    const action = matchEntityAction(path, entity);
    if (action === null) continue;

    const section: BreadcrumbItem | undefined = entity.section
      ? { label: sectionLabels[entity.section] }
      : undefined;

    const parents: BreadcrumbItem[] = [];
    if (section) parents.push(section);

    if (action !== 'list') {
      parents.push({ label: entity.listLabel, to: entity.listPath });
    }

    return {
      section,
      parents,
      pageTitle: titleForAction(entity, action),
      description: entity.description,
    };
  }

  const standalone = standaloneRoutes[path];
  if (standalone) {
    const section = standalone.section
      ? { label: sectionLabels[standalone.section] }
      : undefined;
    return {
      section,
      parents: section ? [section] : [],
      pageTitle: standalone.label,
      description: standalone.description,
    };
  }

  return null;
}

export function mergeBreadcrumbTrail(
  resolved: ResolvedBreadcrumb | null,
  pageTitle: string,
  manualParents?: BreadcrumbItem[],
  description?: string,
): { items: BreadcrumbItem[]; pageTitle: string; description?: string } {
  const dashboard: BreadcrumbItem = { label: 'Dashboard', to: '/' };

  if (manualParents && manualParents.length > 0) {
    return {
      items: [dashboard, ...manualParents],
      pageTitle,
      description: description ?? resolved?.description,
    };
  }

  if (resolved) {
    return {
      items: [dashboard, ...resolved.parents],
      pageTitle,
      description: description ?? resolved.description,
    };
  }

  return {
    items: [dashboard],
    pageTitle,
    description,
  };
}
